import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EncabezadoPagina } from '@xiltrion/ui/bloques/encabezado-pagina';
import { AvatarPersona } from '@xiltrion/ui/bloques/personas';
import { Alert, AlertDescription, AlertTitle } from '@xiltrion/ui/alert';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@xiltrion/ui/card';
import { Field, FieldGroup, FieldLabel } from '@xiltrion/ui/field';
import { Icono } from '@xiltrion/ui/icono';
import { Input } from '@xiltrion/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@xiltrion/ui/input-otp';
import { avisar } from '@xiltrion/ui/sonner';
import { Switch } from '@xiltrion/ui/switch';
import { apiDelete, apiGet, apiPatch, apiPost } from '@/api/client';
import { avisarError } from '@/api/hooks';
import { useSession } from '@/app/session';
import { setThemeMode } from '@/app/theme';

/** Mi perfil: verificación en dos pasos, contraseña, sesiones abiertas y preferencias (tema, correos). */
export function Profile() {
  const qc = useQueryClient();
  const { me, refresh } = useSession();
  const [setup, setSetup] = useState<{ qrDataUrl: string; secret: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [pw, setPw] = useState({ current: '', password: '' });
  const { data: sessions = [] } = useQuery({ queryKey: ['sessions'], queryFn: () => apiGet<Array<{ id: string; createdAt: string; lastActivityAt: string; ip: string | null; userAgent: string | null; current: boolean }>>('/me/sessions') });
  if (!me) return null;
  const pref = (k: string, v: unknown) => void apiPatch('/me/preferences', { [k]: v }).then(() => refresh(), avisarError);
  return (
    <div className="space-y-6 p-6">
      <EncabezadoPagina titulo="Mi perfil" />
      <Card>
        <CardContent className="flex items-center gap-4 pt-6">
          <AvatarPersona persona={{ nombre: me.displayName, color: me.avatarColor }} tamano="lg" />
          <div><div className="font-medium">{me.displayName}</div><div className="text-sm text-muted-foreground">{me.email}</div><div className="mt-1 flex gap-1">{me.roleCodes.map((r) => <Badge key={r} variant="secondary">{r}</Badge>)}</div></div>
        </CardContent>
      </Card>
      {me.authProvider === 'local' && (
        <Card>
          <CardHeader><CardTitle className="text-base">Verificación en dos pasos</CardTitle><CardDescription>Con una app autenticadora (Google Authenticator, Microsoft Authenticator, Authy). También sirve para firmar informes.</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            {me.mfaEnabled ? <Alert variant="success"><Icono nombre="mdi:check-circle-outline" /><AlertTitle>Activa</AlertTitle></Alert> : !setup ? (
              <>
                {me.mfaSetupRequired && <Alert variant="warning"><Icono nombre="mdi:alert-outline" /><AlertDescription>Debes activarla para poder hacer cambios.</AlertDescription></Alert>}
                <Button onClick={() => void apiPost<{ qrDataUrl: string; secret: string }>('/me/mfa/setup').then(setSetup, avisarError)}>Activar</Button>
              </>
            ) : codes ? (
              <Alert variant="info"><AlertTitle>Guarda tus códigos de recuperación</AlertTitle><AlertDescription><p className="mb-2">Cada uno sirve una vez si pierdes el teléfono. No los volverás a ver.</p><div className="tnum grid grid-cols-2 gap-1 font-mono text-sm">{codes.map((c) => <span key={c}>{c}</span>)}</div></AlertDescription></Alert>
            ) : (
              <div className="flex flex-wrap items-start gap-6">
                <img src={setup.qrDataUrl} alt="Código QR para la app autenticadora" className="size-44 rounded-md border bg-white p-2" />
                <div className="space-y-3">
                  <p className="text-sm">Escanea el código y escribe el número que muestra la app.</p>
                  <p className="text-xs text-muted-foreground">¿No puedes escanear? Clave: <code className="break-all">{setup.secret}</code></p>
                  <InputOTP maxLength={6} value={code} onChange={setCode}><InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup></InputOTP>
                  <Button disabled={code.length !== 6} onClick={() => void apiPost<{ recoveryCodes: string[] }>('/me/mfa/enable', { code }).then((r) => { setCodes(r.recoveryCodes); void refresh(); }, avisarError)}>Confirmar</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {me.authProvider === 'local' && (
        <Card>
          <CardHeader><CardTitle className="text-base">Contraseña</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={(e) => { e.preventDefault(); void apiPost('/me/password', pw).then(() => { avisar.success('Contraseña cambiada. Las otras sesiones se cerraron.'); setPw({ current: '', password: '' }); }, avisarError); }}>
              <FieldGroup>
                <Field><FieldLabel htmlFor="cur">Actual</FieldLabel><Input id="cur" type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} autoComplete="current-password" /></Field>
                <Field><FieldLabel htmlFor="new">Nueva (10 caracteres o más)</FieldLabel><Input id="new" type="password" minLength={10} value={pw.password} onChange={(e) => setPw({ ...pw, password: e.target.value })} autoComplete="new-password" /></Field>
                <Button type="submit" disabled={!pw.current || pw.password.length < 10}>Cambiar</Button>
              </FieldGroup>
            </form>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader><CardTitle className="text-base">Preferencias</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between"><label htmlFor="dark" className="text-sm">Modo oscuro</label><Switch id="dark" checked={me.preferences.theme === 'dark'} onCheckedChange={(v) => { setThemeMode(v ? 'dark' : 'light'); pref('theme', v ? 'dark' : 'light'); }} /></div>
          <div className="flex items-center justify-between"><label htmlFor="mail" className="text-sm">Correo cuando me mencionan o asignan</label><Switch id="mail" checked={me.preferences.emailNotifications !== false} onCheckedChange={(v) => pref('emailNotifications', v)} /></div>
          <div className="flex items-center justify-between"><label htmlFor="dig" className="text-sm">Resumen diario por correo</label><Switch id="dig" checked={me.preferences.dailyDigest !== false} onCheckedChange={(v) => pref('dailyDigest', v)} /></div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Sesiones abiertas</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {sessions.map((s) => (
            <div key={s.id} className="flex items-center gap-2 text-sm">
              <Icono nombre="mdi:earth" className="text-muted-foreground" />
              <div className="min-w-0 flex-1"><div className="truncate">{s.userAgent ?? 'Navegador'}</div><div className="text-xs text-muted-foreground">{s.ip ?? ''} · activa {new Date(s.lastActivityAt).toLocaleString('es-CO')}</div></div>
              {s.current ? <Badge variant="success-light">Esta</Badge> : <Button variant="ghost" size="sm" onClick={() => void apiDelete(`/me/sessions/${s.id}`).then(() => qc.invalidateQueries({ queryKey: ['sessions'] }), avisarError)}>Cerrar</Button>}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
